import * as React from 'react';
import { useEffect, useState, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { FC } from 'react';
import { UserName } from './UserNameComponent';
import {
	contactsRestCrudThunksForBuyer,
	contactsRestCrudThunksForSupplier,
} from '../../../thunks/contacts_thunks';
import { withUserType } from '../../withUserType';
import { ContactRequestBatcher } from './ContactRequestBatcher';
import { ROLE_TYPES } from '../../../utils/DataConstants';

// Create batcher instances per user type
const batchers = new Map<string, ContactRequestBatcher>();

const getBatcher = (userType: string, getContactsFn: (emails: string[]) => Promise<any>) => {
	if (!batchers.has(userType)) {
		batchers.set(userType, new ContactRequestBatcher(getContactsFn, userType));
	}
	return batchers.get(userType);
};

interface UserNameComponentProps {
	contact: any;
	userType?: 'buyer' | 'supplier';
	mode?: 'card' | 'inline' | 'container' | 'avatar' | 'dropdown';
	nameFormat?: string;
	secondaryField?: string;
	showAvatar?: boolean;
	showNameWithAvatar?: boolean;
	size?: 'default' | 'large' | 'small';
	allowHover?: boolean;
	popoverMode?: 'simple' | 'full';
	placement?: string;
	underline?: boolean;
	details?: any;
	extraDetails?: any;
	renderName?: any;
	onClick?: any;
	account?: any;
	status?: any;
}

interface UserNameWithEmailProps extends Omit<UserNameComponentProps, 'contact'> {
	email: string;
	contact?: any;
	allContacts?: any;
	sessionUserType?: string;
	getContacts?: any;
	/**
	 * Shown when the address resolves to no contact — a deactivated user, one
	 * outside this tenant, a service account.
	 *
	 * Without it this renders nothing, which is right in a column where a blank
	 * cell reads as "nobody" but wrong mid-sentence: "Paused by " with the name
	 * missing is worse than the address it was going to replace. Omitted, the
	 * behaviour is unchanged.
	 */
	fallback?: React.ReactNode;
}

const UserNameWithEmailComponent: FC<UserNameWithEmailProps> = ({
	email,
	contact: providedContact,
	userType,
	allContacts,
	getContacts,
	fallback,
	...otherProps
}) => {
	const [isFetching, setIsFetching] = useState(false);
	const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);
	const batcherRef = useRef<ContactRequestBatcher | null>(null);

	const contact = useMemo(() => {
		if (providedContact) {
			return providedContact;
		}

		if (!email) {
			return null;
		}

		if (allContacts && allContacts.records && allContacts.records[email]) {
			return allContacts.records[email];
		}

		return null;
	}, [providedContact, email, allContacts]);

	useEffect(() => {
		// Initialize batcher for this user type
		if (!batcherRef.current && getContacts && userType) {
			batcherRef.current = getBatcher(userType, (emails: string[]) => {
				return getContacts(emails);
			});
		}
	}, [getContacts, userType]);

	useEffect(() => {
		if (!contact && email && !isFetching && !hasAttemptedFetch && batcherRef.current) {
			setIsFetching(true);
			setHasAttemptedFetch(true);

			batcherRef.current.requestContact(email, () => {
				setIsFetching(false);
			});
		} else if (contact) {
			setIsFetching(false);
		}
	}, [email, contact, isFetching, hasAttemptedFetch]);

	useEffect(() => {
		setHasAttemptedFetch(false);
	}, [email]);

	if (contact) {
		return <UserName contact={contact} userType={userType} {...otherProps} />;
	}

	return fallback ? <>{fallback}</> : null;
};

const mapStateToProps = (state: any) => ({
	allContacts: state.all_contacts,
});

const mapDispatchToProps = (dispatch: any, ownProps: any) => ({
	getContacts: (ids) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? contactsRestCrudThunksForSupplier.readMultiple(
						ids,
						'workOrdersIndex',
						null,
						null,
						null,
						true
				  )
				: contactsRestCrudThunksForBuyer.readMultiple(
						ids,
						'workOrdersIndex',
						null,
						null,
						null,
						true
				  )
		),
});

export const UserNameWithEmail = withUserType(
	connect(mapStateToProps, mapDispatchToProps)(UserNameWithEmailComponent)
);
